/**
 * Reusable Animation Engine
 *
 * Based on the working implementation from Splash.vue
 * Handles object movement, collision detection, and visual effects within a defined bounding box.
 *
 * Features:
 * - Object collision detection and realistic bouncing
 * - Wall collision with proper physics
 * - Configurable effects: rotation, scale, opacity, blur
 * - Staggered effect starts
 * - Responsive bounding box calculation
 * - Performance optimized with requestAnimationFrame
 */

export interface AnimationObject {
    element: HTMLElement | SVGElement;
    x: number;
    y: number;
    vx: number;
    vy: number;
    rotation: number;
    rotationDirection: 'clockwise' | 'anticlockwise'; // Individual rotation direction
    rotationSpeed: number; // Rotation speed per frame
    scale: number;
    opacity: number;
    radius: number;
    hasEnteredBounds: boolean;
    // Effect cycling properties
    effectTime: number;
    effects: {
        scale: { from: number; to: number; speed: number; };
        opacity: { from: number; to: number; speed: number; };
        blur: { from: number; to: number; speed: number; };
        color: { from: string; to: string; speed: number; };
    };
}

export interface BoundingBox {
    width: number;
    height: number;
    left: number;
    top: number;
}

export interface AnimationConfig {
    containerSelector: string;
    elementSelector: string;
    enableLogging?: boolean;
    effectDefaults?: {
        scale: { from: number; to: number; speed: number };
        opacity: { from: number; to: number; speed: number };
        blur: { from: number; to: number; speed: number };
        color: { from: string; to: string; speed: number };
    };
    autoStart?: boolean;
    rotationDirections?: ('clockwise' | 'anticlockwise')[]; // Array of rotation directions for each object
    rotationSpeed?: number; // Rotation speed per frame for all objects
    animationSpeed?: number; // Overall animation speed multiplier (low = slow, high = fast)
}

/**
 * Animation Engine Class
 * Handles the core animation logic, collision detection, and effect management
 */
export class AnimationEngine {
    private isRunning: boolean = false;
    private animationId: number | null = null;
    private objects: AnimationObject[] = [];
    private boundingBox: BoundingBox = { width: 0, height: 0, left: 0, top: 0 };
    private resizeObserver: ResizeObserver | null = null;
    private config: Required<AnimationConfig>;

    constructor(config: AnimationConfig) {
        this.config = {
            containerSelector: config.containerSelector,
            elementSelector: config.elementSelector,
            enableLogging: config.enableLogging ?? false,
            effectDefaults: config.effectDefaults ?? {
                scale: { from: 0.5, to: 1.5, speed: 0.02 },
                opacity: { from: 0.3, to: 1, speed: 0.01 },
                blur: { from: 0, to: 5, speed: 0.1 },
                color: { from: '#a250d6', to: '#ffffff', speed: 3 }
            },
            autoStart: config.autoStart ?? true,
            rotationDirections: config.rotationDirections ?? [],
            rotationSpeed: config.rotationSpeed ?? 1.0, // Default rotation speed
            animationSpeed: config.animationSpeed ?? 1.0 // Default animation speed multiplier
        };
    }

    // Helper function for conditional logging
    private log(message: string, ...args: unknown[]): void {
        if (this.config.enableLogging) {
            console.log(message, ...args);
        }
    }

    /**
     * Interpolate between two colors
     */
    private interpolateColor(fromColor: string, toColor: string, progress: number): string {
        // Parse hex colors to RGB
        const parseHex = (hex: string): [number, number, number] => {
            const clean = hex.replace('#', '');
            return [
                parseInt(clean.substr(0, 2), 16),
                parseInt(clean.substr(2, 2), 16),
                parseInt(clean.substr(4, 2), 16)
            ];
        };

        // Convert RGB back to hex
        const toHex = (r: number, g: number, b: number): string => {
            const componentToHex = (c: number) => {
                const hex = Math.round(Math.max(0, Math.min(255, c))).toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            };
            return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
        };

        try {
            const [r1, g1, b1] = parseHex(fromColor);
            const [r2, g2, b2] = parseHex(toColor);

            const r = r1 + (r2 - r1) * progress;
            const g = g1 + (g2 - g1) * progress;
            const b = b1 + (b2 - b1) * progress;

            return toHex(r, g, b);
        } catch (error) {
            // Fallback to fromColor if parsing fails
            this.log('Color interpolation error:', error);
            return fromColor;
        }
    }

    /**
     * Calculate bounding box from container
     */
    private calculateBoundingBox(container: HTMLElement): void {
        const style = getComputedStyle(container);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingTop = parseFloat(style.paddingTop) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const paddingBottom = parseFloat(style.paddingBottom) || 0;

        const containerWidth = container.offsetWidth - paddingLeft - paddingRight;
        const containerHeight = container.offsetHeight - paddingTop - paddingBottom;

        this.boundingBox = {
            width: containerWidth,
            height: containerHeight,
            left: paddingLeft,
            top: paddingTop
        };

        this.log('📦 Bounding box calculated:', this.boundingBox);
    }

    /**
     * Handle container resize - recalculate bounding box and reposition objects if needed
     */
    private handleResize(container: HTMLElement): void {
        const oldBoundingBox = { ...this.boundingBox };
        this.calculateBoundingBox(container);

        // Check if any objects are now outside the new bounds and need repositioning
        this.objects.forEach(obj => {
            let needsRepositioning = false;

            // Check if object is outside new bounds
            if (obj.x - obj.radius < this.boundingBox.left ||
                obj.x + obj.radius > this.boundingBox.left + this.boundingBox.width ||
                obj.y - obj.radius < this.boundingBox.top ||
                obj.y + obj.radius > this.boundingBox.top + this.boundingBox.height) {
                needsRepositioning = true;
            }

            // If object was inside bounds but now outside, reposition it
            if (needsRepositioning && obj.hasEnteredBounds) {
                // Move object to safe position inside new bounds
                obj.x = Math.max(
                    this.boundingBox.left + obj.radius,
                    Math.min(this.boundingBox.left + this.boundingBox.width - obj.radius, obj.x)
                );
                obj.y = Math.max(
                    this.boundingBox.top + obj.radius,
                    Math.min(this.boundingBox.top + this.boundingBox.height - obj.radius, obj.y)
                );

                this.log('🔄 Repositioned object inside new bounds:', obj.element.className);
            }

            // Note: We maintain constant object speeds regardless of container size changes
            // This ensures animation speed remains consistent and doesn't compound over time
        });

        this.log('✅ Resize handling complete:', {
            oldSize: `${oldBoundingBox.width}x${oldBoundingBox.height}`,
            newSize: `${this.boundingBox.width}x${this.boundingBox.height}`
        });
    }

    /**
     * Initialize animation with proper bounding box calculation
     */
    public initialize(): boolean {
        try {
            // Find container
            const container = document.querySelector(this.config.containerSelector) as HTMLElement;
            if (!container) {
                this.log('⚠️ Animation container not found');
                return false;
            }

            // Calculate proper bounding box (container dimensions, not viewport)
            this.calculateBoundingBox(container);

            // Setup resize observer for responsive behavior
            this.resizeObserver = new ResizeObserver(() => {
                this.log('📐 Container resized, recalculating bounding box and repositioning objects...');
                this.handleResize(container);
            });
            this.resizeObserver.observe(container);

            // Find plus elements
            const plusElements = document.querySelectorAll(this.config.elementSelector);
            if (plusElements.length === 0) {
                this.log('⚠️ No elements found to animate');
                return false;
            }

            // Initialize objects with corner placement (favor corners for 4 or fewer objects)
            this.objects = Array.from(plusElements).map((element, index) => {
                const margin = 150;
                let startX: number, startY: number;

                // Favor corners for 4 or fewer objects as per specification
                if (plusElements.length <= 4) {
                    const corners = [
                        { x: this.boundingBox.left - margin, y: this.boundingBox.top - margin },
                        { x: this.boundingBox.left + this.boundingBox.width + margin, y: this.boundingBox.top - margin },
                        { x: this.boundingBox.left + this.boundingBox.width + margin, y: this.boundingBox.top + this.boundingBox.height + margin },
                        { x: this.boundingBox.left - margin, y: this.boundingBox.top + this.boundingBox.height + margin }
                    ];
                    const corner = corners[index] || corners[0];
                    startX = corner.x;
                    startY = corner.y;
                } else {
                    // Distribute around perimeter for more objects
                    const angle = (index / plusElements.length) * 2 * Math.PI;
                    const radius = Math.max(this.boundingBox.width, this.boundingBox.height) / 2 + margin;
                    const centerX = this.boundingBox.left + this.boundingBox.width / 2;
                    const centerY = this.boundingBox.top + this.boundingBox.height / 2;

                    startX = centerX + Math.cos(angle) * radius;
                    startY = centerY + Math.sin(angle) * radius;
                }

                // Calculate velocity toward center with bias
                const centerBias = 0.7;
                const centerX = this.boundingBox.left + this.boundingBox.width / 2;
                const centerY = this.boundingBox.top + this.boundingBox.height / 2;

                const targetX = centerX + (Math.random() - 0.5) * this.boundingBox.width * (1 - centerBias);
                const targetY = centerY + (Math.random() - 0.5) * this.boundingBox.height * (1 - centerBias);

                const dx = targetX - startX;
                const dy = targetY - startY;
                const length = Math.sqrt(dx * dx + dy * dy);

                // Normalize velocity to ensure consistent speed for all objects
                const baseSpeed = 2.0; // Consistent base speed for all objects
                const normalizedVx = length > 0 ? (dx / length) * baseSpeed : baseSpeed;
                const normalizedVy = length > 0 ? (dy / length) * baseSpeed : 0;

                const htmlElement = element as HTMLElement;
                htmlElement.style.position = 'absolute';
                htmlElement.style.willChange = 'transform, opacity, filter';
                htmlElement.style.transformOrigin = 'center';
                htmlElement.style.visibility = 'hidden'; // Keep hidden initially to prevent FOUC

                // Get object dimensions
                const rect = element.getBoundingClientRect();
                const radius = Math.max(rect.width || 50, rect.height || 50) / 2;

                this.log(`🎯 Object ${index + 1} initialized at:`, {
                    x: startX,
                    y: startY,
                    vx: normalizedVx.toFixed(3),
                    vy: normalizedVy.toFixed(3),
                    speed: Math.sqrt(normalizedVx * normalizedVx + normalizedVy * normalizedVy).toFixed(3)
                });

                // Define effect configurations with FROM and TO values
                const effects = {
                    scale: { ...this.config.effectDefaults.scale },
                    opacity: { ...this.config.effectDefaults.opacity },
                    blur: { ...this.config.effectDefaults.blur },
                    color: { ...this.config.effectDefaults.color }
                };

                // Set initial rotation direction - respect the configured array
                let rotationDirection: 'clockwise' | 'anticlockwise';

                if (this.config.rotationDirections &&
                    this.config.rotationDirections.length > index &&
                    this.config.rotationDirections[index] &&
                    (this.config.rotationDirections[index] === 'clockwise' || this.config.rotationDirections[index] === 'anticlockwise')) {
                    // Use configured initial direction
                    rotationDirection = this.config.rotationDirections[index];
                } else {
                    // Default fallback
                    rotationDirection = 'clockwise';
                }

                this.log(`🔄 Object ${index + 1} rotation:`, {
                    direction: rotationDirection,
                    rotationSpeed: this.config.rotationSpeed,
                    element: element.className
                });

                return {
                    element: htmlElement,
                    x: startX,
                    y: startY,
                    vx: normalizedVx, // Use consistent normalized velocity
                    vy: normalizedVy, // Use consistent normalized velocity
                    rotation: 0,
                    rotationDirection: rotationDirection,
                    rotationSpeed: this.config.rotationSpeed,
                    scale: effects.scale.from, // Start at FROM value
                    opacity: effects.opacity.from, // Start at FROM value
                    radius,
                    hasEnteredBounds: false,
                    effectTime: 0, // Track effect animation time
                    effects // Store effect configurations
                };
            });

            this.log(`🎬 Animation initialized with ${this.objects.length} objects`);

            // Auto-start animation if configured
            if (this.config.autoStart) {
                this.start();
            }

            return true;

        } catch (error) {
            console.error('Failed to initialize animation:', error);
            return false;
        }
    }

    /**
     * Calculate the effective collision radius accounting for scale and rotation
     */
    private getEffectiveRadius(obj: AnimationObject): number {
        // Account for current scale when calculating collision boundaries
        const scaledRadius = obj.radius * obj.scale;

        // For rotated objects, use the diagonal to ensure we don't miss collision points
        // This is conservative but prevents objects from clipping through walls
        const rotationFactor = Math.abs(Math.sin(obj.rotation * Math.PI / 180)) + Math.abs(Math.cos(obj.rotation * Math.PI / 180));

        return scaledRadius * Math.max(1, rotationFactor * 0.7); // 0.7 factor to avoid being too conservative
    }

    /**
     * Get the effective bounding box for an object accounting for its actual rendered size
     */
    private getObjectBounds(obj: AnimationObject): {
        left: number;
        right: number;
        top: number;
        bottom: number;
        effectiveRadius: number;
    } {
        const effectiveRadius = this.getEffectiveRadius(obj);

        return {
            left: obj.x - effectiveRadius,
            right: obj.x + effectiveRadius,
            top: obj.y - effectiveRadius,
            bottom: obj.y + effectiveRadius,
            effectiveRadius
        };
    }

    /**
     * Check if object has entered the bounding box using effective radius
     */
    private isInsideBounds(obj: AnimationObject): boolean {
        const bounds = this.getObjectBounds(obj);

        return bounds.right > this.boundingBox.left &&
               bounds.left < this.boundingBox.left + this.boundingBox.width &&
               bounds.bottom > this.boundingBox.top &&
               bounds.top < this.boundingBox.top + this.boundingBox.height;
    }

    /**
     * Handle wall collisions with proper scale and rotation consideration
     */
    private handleWallCollisions(obj: AnimationObject): void {
        let collided = false;
        const bounds = this.getObjectBounds(obj);
        const effectiveRadius = bounds.effectiveRadius;

        // Left wall collision
        if (bounds.left <= this.boundingBox.left) {
            obj.x = this.boundingBox.left + effectiveRadius;
            obj.vx = Math.abs(obj.vx); // Maintain constant speed - no energy loss
            collided = true;
            this.log('💥 Left wall collision - effective radius:', effectiveRadius, 'scale:', obj.scale.toFixed(2));
        }

        // Right wall collision
        if (bounds.right >= this.boundingBox.left + this.boundingBox.width) {
            obj.x = this.boundingBox.left + this.boundingBox.width - effectiveRadius;
            obj.vx = -Math.abs(obj.vx); // Maintain constant speed - no energy loss
            collided = true;
            this.log('💥 Right wall collision - effective radius:', effectiveRadius, 'scale:', obj.scale.toFixed(2));
        }

        // Top wall collision
        if (bounds.top <= this.boundingBox.top) {
            obj.y = this.boundingBox.top + effectiveRadius;
            obj.vy = Math.abs(obj.vy); // Maintain constant speed - no energy loss
            collided = true;
            this.log('💥 Top wall collision - effective radius:', effectiveRadius, 'scale:', obj.scale.toFixed(2));
        }

        // Bottom wall collision
        if (bounds.bottom >= this.boundingBox.top + this.boundingBox.height) {
            obj.y = this.boundingBox.top + this.boundingBox.height - effectiveRadius;
            obj.vy = -Math.abs(obj.vy); // Maintain constant speed - no energy loss
            collided = true;
            this.log('💥 Bottom wall collision - effective radius:', effectiveRadius, 'scale:', obj.scale.toFixed(2));
        }

        // Handle corner collisions - if object hits both walls simultaneously
        if (collided) {
            // Recalculate bounds after position adjustment
            const newBounds = this.getObjectBounds(obj);

            // Check if we're still outside bounds after correction (corner case)
            if (newBounds.left < this.boundingBox.left ||
                newBounds.right > this.boundingBox.left + this.boundingBox.width ||
                newBounds.top < this.boundingBox.top ||
                newBounds.bottom > this.boundingBox.top + this.boundingBox.height) {

                // Force object to safe position in center of container
                obj.x = this.boundingBox.left + this.boundingBox.width / 2;
                obj.y = this.boundingBox.top + this.boundingBox.height / 2;

                // Reverse both velocities for corner bounce - maintain speed magnitude
                const currentSpeed = Math.sqrt(obj.vx * obj.vx + obj.vy * obj.vy);
                obj.vx = -obj.vx;
                obj.vy = -obj.vy;
                // Normalize to maintain original speed
                const newSpeed = Math.sqrt(obj.vx * obj.vx + obj.vy * obj.vy);
                if (newSpeed > 0) {
                    obj.vx = (obj.vx / newSpeed) * currentSpeed;
                    obj.vy = (obj.vy / newSpeed) * currentSpeed;
                }

                this.log('🏃 Corner collision - moved to center:', obj.element.className);
            }

            // Simple direction change on wall collision (no random factors)
            // Only change direction after object has been inside bounds for a while
            // This prevents immediate direction changes when objects first enter from outside
            if (obj.hasEnteredBounds) {
                if (obj.rotationDirection === 'clockwise') {
                    obj.rotationDirection = 'anticlockwise';
                } else {
                    obj.rotationDirection = 'clockwise';
                }
                this.log('💥 Wall collision - direction changed to:', obj.rotationDirection);
            }
        }
    }

    /**
     * Handle object-to-object collisions with proper scale consideration
     */
    private handleObjectCollisions(obj: AnimationObject, index: number): void {
        this.objects.forEach((otherObj, otherIndex) => {
            if (otherIndex === index || !otherObj.hasEnteredBounds) return;

            const dx = obj.x - otherObj.x;
            const dy = obj.y - otherObj.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Use effective radius that accounts for current scale
            const objEffectiveRadius = this.getEffectiveRadius(obj);
            const otherObjEffectiveRadius = this.getEffectiveRadius(otherObj);
            const minDistance = objEffectiveRadius + otherObjEffectiveRadius + 2; // Add 2px safety margin

            if (distance < minDistance && distance > 0) {
                // Collision detected
                const overlap = minDistance - distance + 1; // Extra 1px to ensure separation
                const separationX = (dx / distance) * overlap * 0.5;
                const separationY = (dy / distance) * overlap * 0.5;

                // Separate objects
                obj.x += separationX;
                obj.y += separationY;
                otherObj.x -= separationX;
                otherObj.y -= separationY;

                // Exchange velocities for realistic collision
                const tempVx = obj.vx;
                const tempVy = obj.vy;
                obj.vx = otherObj.vx;
                obj.vy = otherObj.vy;
                otherObj.vx = tempVx;
                otherObj.vy = tempVy;

                // Simple direction change on object collision (no random factors)
                // Only change direction after both objects have been inside bounds
                if (obj.hasEnteredBounds && otherObj.hasEnteredBounds) {
                    if (obj.rotationDirection === 'clockwise') {
                        obj.rotationDirection = 'anticlockwise';
                    } else {
                        obj.rotationDirection = 'clockwise';
                    }

                    if (otherObj.rotationDirection === 'clockwise') {
                        otherObj.rotationDirection = 'anticlockwise';
                    } else {
                        otherObj.rotationDirection = 'clockwise';
                    }

                    this.log('💥 Object collision - directions reversed');
                }
            }
        });
    }

    /**
     * Main animation loop with proper collision detection
     */
    private animate = (): void => {
        if (!this.isRunning) return;

        this.objects.forEach((obj, index) => {
            // Update position with animation speed multiplier
            obj.x += obj.vx * this.config.animationSpeed;
            obj.y += obj.vy * this.config.animationSpeed;

            // Check if entered bounds
            if (!obj.hasEnteredBounds) {
                if (this.isInsideBounds(obj)) {
                    obj.hasEnteredBounds = true;
                    this.log('🎯 Object entered bounds:', obj.element.className);
                }
            }

            // Handle collisions ONLY if inside bounds (as per specification)
            if (obj.hasEnteredBounds) {
                this.handleWallCollisions(obj);
                this.handleObjectCollisions(obj, index);
            }

            // Update effects - use rotation speed with direction and animation speed multiplier
            // Note: In CSS, positive rotation values go anticlockwise, negative values go clockwise
            const rotationIncrement = obj.rotationDirection === 'clockwise'
                ? -obj.rotationSpeed * this.config.animationSpeed  // Use negative for clockwise
                : obj.rotationSpeed * this.config.animationSpeed;  // Use positive for anticlockwise
            obj.rotation += rotationIncrement;

            // Update effect time (accounting for animation speed)
            const deltaTime = (1/60) * this.config.animationSpeed; // Assume 60fps for consistent timing, scaled by animation speed
            obj.effectTime += deltaTime;

            // Make object visible when effects start (prevents FOUC)
            if (obj.element.style.visibility === 'hidden') {
                obj.element.style.visibility = 'visible';
                this.log('👁️ Object made visible:', obj.element.className);
            }

            // Calculate effect progress (0 to 1) with cycling
            const progress = (Math.sin(obj.effectTime / obj.effects.scale.speed * Math.PI * 2) + 1) / 2; // Sine wave from 0 to 1

            // Calculate synchronized effect values using the same progress
            obj.scale = obj.effects.scale.from + (obj.effects.scale.to - obj.effects.scale.from) * progress;
            obj.opacity = obj.effects.opacity.from + (obj.effects.opacity.to - obj.effects.opacity.from) * progress;
            const blur = obj.effects.blur.from + (obj.effects.blur.to - obj.effects.blur.from) * progress;
            const color = this.interpolateColor(obj.effects.color.from, obj.effects.color.to, progress);

            // Apply visual updates with proper positioning and synchronized effects
            obj.element.style.left = `${obj.x - obj.radius}px`;
            obj.element.style.top = `${obj.y - obj.radius}px`;
            obj.element.style.transform = `scale(${obj.scale}) rotate(${obj.rotation}deg)`;
            obj.element.style.opacity = `${obj.opacity}`;
            obj.element.style.filter = `blur(${blur}px)`;

            // Apply color to stroke (for SVG paths) or color (for text elements)
            if (obj.element instanceof SVGElement) {
                const paths = obj.element.querySelectorAll('path');
                paths.forEach(path => {
                    path.setAttribute('stroke', color);
                });
            } else {
                obj.element.style.color = color;
            }
        });

        this.animationId = requestAnimationFrame(this.animate);
    };

    /**
     * Start animation
     */
    public start(): void {
        if (this.isRunning) return;

        this.isRunning = true;
        this.log('▶️ Animation started');
        this.animate();
    }

    /**
     * Stop animation
     */
    public stop(): void {
        this.isRunning = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // Reset object visibility to prevent FOUC on restart
        this.objects.forEach(obj => {
            obj.element.style.visibility = 'hidden';
            obj.effectTime = 0; // Reset effect time for clean restart
            obj.hasEnteredBounds = false; // Reset bounds state
        });

        this.log('⏹️ Animation stopped');
    }

    /**
     * Toggle animation
     */
    public toggle(): void {
        if (this.isRunning) {
            this.stop();
        } else {
            this.start();
        }
    }

    /**
     * Setup keyboard controls for development (Ctrl+Space to toggle)
     */
    public setupKeyboardControls(): void {
        const handleKeyPress = (event: KeyboardEvent): void => {
            if (event.key === ' ' && event.ctrlKey) {
                event.preventDefault();
                this.toggle();
            }
        };

        if (typeof window !== 'undefined' && import.meta.env.DEV) {
            document.addEventListener('keydown', handleKeyPress);
        }
    }

    /**
     * Cleanup resources
     */
    public destroy(): void {
        this.stop();

        // Cleanup resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Reset all object styles to clean state
        this.objects.forEach(obj => {
            obj.element.style.visibility = 'hidden';
            obj.element.style.position = '';
            obj.element.style.left = '';
            obj.element.style.top = '';
            obj.element.style.transform = '';
            obj.element.style.opacity = '';
            obj.element.style.filter = '';
            obj.element.style.willChange = '';
            obj.element.style.transformOrigin = '';
        });

        this.objects = [];

        this.log('🧹 Animation engine destroyed');
    }
}
